// City buildings: a cluster on each city's hex, and district hexes around the largest cities,
// sized by the snapshot's population (cities.json: GHSL urban centres, dated). A symbol of the
// city's size, not a literal skyline: counts grow with log(population).
//
//   population ─► blocks  3 + 4·log10(pop / 10k)      (10k: 3 · 1M: 11 · 40M: 17)
//              ─► towers  from 5M: 2, +1 per doubling
//              ─► districts on neighbouring land: 1M: 2 · 5M: 4 · 15M: 6
//   capital    ─► the generic capitol at the centre (landmark.capitol, not a real building)
//   climate    ─► the building set (STYLES): a decorative variety keyed by the hex's terrain only
//
// Placed on the main thread from each snapshot (cities change with the date), grouped by terrain
// chunk and block like the worker's props, so the feature layer culls and merges them the same way.

import { hexCenter, hexCol, hexRow, neighbour } from '../../core/grid.ts';
import { HILL, TERRAIN, type City } from '../../core/schema.ts';
import type { WorldSnapshot } from '../../world/api.ts';
import type { PhysicalGrid } from '../hexdata.ts';
import { HEX_KIND, hexHash, type HexShapes } from '../terrain/heights.ts';
import { CHUNK_SIZE, chunkKey, crownProfile } from '../terrain/mesher.ts';
import { BUILDING, blockOf, clear, keepOuts, packBlocks, peakFootprint, type FeatureData } from './placement.ts';

const BLOCKS_BASE = 3;
const BLOCKS_PER_DECADE = 4;
const POP_FLOOR = 10_000;
const TOWER_POP = 5_000_000;
const TOWERS_BASE = 2;
const DISTRICT_STEPS: ReadonlyArray<readonly [pop: number, hexes: number]> = [[15_000_000, 6], [5_000_000, 4], [1_000_000, 2]];
const DISTRICT_SHARE = 0.35; // blocks per district, as a share of the centre's
const OLDTOWN = 2;
const OLDTOWN_POP = 1_000_000; // smaller towns get only the light blocks (old-town houses are ~1k triangles)

// Cities below this population collapse to a flat district decal from a camera height of ~40
// (layer.ts): only larger cities keep a 3D silhouette. Their owner is stored as -(owner + 1).
const MINOR_POP = 300_000;

// A hero peak's model reaches this many footprint radii; buildings keep this clear of it.
const PEAK_REACH = 1.4;
const PEAK_GAP = 0.08;

type Tier = 'major' | 'minor';

/** The buildings a city is built from, by the climate of its hex (decoration, not data). */
interface Style {
  old: readonly number[];
  block: readonly number[];
  tower: readonly number[];
}

const STYLES: Record<'temperate' | 'arid' | 'cold', Style> = {
  temperate: {
    old: BUILDING.OLDTOWN,
    block: [BUILDING.BLOCK[0], BUILDING.MIDRISE[0], BUILDING.MIDRISE[1], BUILDING.OFFICE[0], BUILDING.BLOCK[1], BUILDING.MIDRISE[2], BUILDING.OFFICE[1]],
    tower: [BUILDING.TOWER[0], BUILDING.TOWER[1], BUILDING.SKYSCRAPER[0]],
  },
  arid: {
    old: [BUILDING.OLDTOWN[0], BUILDING.HOUSE[0]],
    block: [BUILDING.BLOCK[3], BUILDING.BLOCK[1], BUILDING.HOUSE[0], BUILDING.HOUSE[1], BUILDING.BLOCK[2]],
    tower: [BUILDING.SKYSCRAPER[1], BUILDING.SKYSCRAPER[2]],
  },
  cold: {
    old: [BUILDING.OLDTOWN[1], BUILDING.HOUSE[2]],
    block: [BUILDING.HOUSE[2], BUILDING.HOUSE[1], BUILDING.BLOCK[2], BUILDING.MIDRISE[0]],
    tower: [BUILDING.TOWER[1], BUILDING.SKYSCRAPER[0]],
  },
};

function styleOf(grid: PhysicalGrid, t: number): Style {
  const terrain = grid.terrain[t];
  if (terrain === TERRAIN.DESERT) {
    return STYLES.arid;
  }
  return terrain === TERRAIN.TUNDRA || terrain === TERRAIN.SNOW ? STYLES.cold : STYLES.temperate;
}

const SCALE = [1.55, 1.95] as const;
const TOWER_SCALE = [1.35, 1.7] as const;
const CAPITOL_SCALE = 1.9;
const SPACING = 0.19;
const REACH = 0.7;
const JITTER = 0.05;
const SINK = 0.02;
const AMP_SINK = 1; // extra sink per unit of the hex's noise amplitude

/** Candidate points in a hex, nearest the centre first. */
const POINTS: ReadonlyArray<readonly [number, number]> = (() => {
  const pts: Array<[number, number]> = [];
  for (let r = -6; r <= 6; r++) {
    for (let q = -6; q <= 6; q++) {
      const x = SPACING * (q + r / 2);
      const z = SPACING * r * (Math.sqrt(3) / 2);
      if (Math.hypot(x, z) <= REACH) {
        pts.push([x, z]);
      }
    }
  }
  return pts.sort((a, b) => Math.hypot(a[0], a[1]) - Math.hypot(b[0], b[1]));
})();

/** Ground height at local offset (lx, lz) of a land hex, from the height model (no mesh needed). */
function groundAt(shapes: HexShapes, t: number, lx: number, lz: number): number {
  const r = Math.min(1, Math.hypot(lx, lz));
  const crown = shapes.crown[t] * shapes.crownScale[t] * crownProfile(r);
  return shapes.base[t] + shapes.lift[t] + crown - SINK - AMP_SINK * shapes.amp[t];
}

function blocksFor(pop: number): number {
  return Math.round(BLOCKS_BASE + BLOCKS_PER_DECADE * Math.log10(Math.max(pop, POP_FLOOR) / POP_FLOOR));
}

function towersFor(pop: number): number {
  return pop < TOWER_POP ? 0 : TOWERS_BASE + Math.floor(Math.log2(pop / TOWER_POP));
}

function districtsFor(pop: number): number {
  return DISTRICT_STEPS.find(([p]) => pop >= p)?.[1] ?? 0;
}

interface Stamp {
  slot: number;
  scale: readonly [number, number];
}

const FNV_OFFSET = 2166136261;
const FNV_PRIME = 16777619;
const POP_STEPS_PER_OCTAVE = 4;

/** One FNV-1a step over a 32-bit word. */
function fnv(h: number, word: number): number {
  return Math.imul(h ^ word, FNV_PRIME);
}

/** FNV-1a over the UTF-16 units of a string. */
function fnvText(h: number, text: string): number {
  for (let i = 0; i < text.length; i++) {
    h = fnv(h, text.charCodeAt(i));
  }
  return h;
}

/**
 * Changes whenever what placeCities reads changes: which cities exist, their size step, the owner
 * under them, the capital flag (the capitol model) and the name and country (a rename or a
 * transfer must not keep the old cluster: a city that changes hands changes its roof colour).
 */
export function citySignature(s: Pick<WorldSnapshot, 'cities' | 'owner'>): string {
  let h = FNV_OFFSET;
  for (const c of s.cities) {
    h = fnv(h, c.id);
    h = fnv(h, Math.round(Math.log2(Math.max(1, c.pop)) * POP_STEPS_PER_OCTAVE));
    h = fnv(h, s.owner[c.hex]);
    h = fnv(h, c.capital ? 1 : 0);
    h = fnvText(h, c.name);
    h = fnvText(h, c.iso3);
  }
  return `${s.cities.length}:${h >>> 0}`;
}

/** Buildings for every city, grouped by terrain chunk key. */
export function placeCities(cities: readonly City[], grid: PhysicalGrid, shapes: HexShapes, owner: Uint16Array): Map<number, FeatureData> {
  const perChunk = new Map<number, Map<number, number[]>>();
  const used = new Set<number>();
  const emit = (t: number, stamps: readonly Stamp[], salt: number, tier: Tier): void => {
    if (shapes.kind[t] !== HEX_KIND.LAND || used.has(t)) {
      return;
    }
    used.add(t);
    const col = hexCol(t);
    const row = hexRow(t);
    const cc = Math.floor(col / CHUNK_SIZE);
    const rc = Math.floor(row / CHUNK_SIZE);
    const key = chunkKey(cc, rc);
    let blocks = perChunk.get(key);
    if (!blocks) {
      blocks = new Map();
      perChunk.set(key, blocks);
    }
    const block = blockOf(t, cc * CHUNK_SIZE, rc * CHUNK_SIZE);
    let out = blocks.get(block);
    if (!out) {
      out = [];
      blocks.set(block, out);
    }
    const c = hexCenter(col, row);
    const k = keepOuts(grid, t);
    const spin = hexHash(t, 40 + salt) * Math.PI * 2;

    // A mountain hex keeps its hero peak's footprint free.
    const peak = grid.hill[t] === HILL.MOUNTAINS ? peakFootprint(grid, t, c.x, c.z) : null;
    const peakX = peak ? peak.x - c.x : 0;
    const peakZ = peak ? peak.z - c.z : 0;
    const peakClear = peak ? peak.r * PEAK_REACH + PEAK_GAP : 0;
    const ownerCode = tier === 'minor' ? -(owner[t] + 1) : owner[t];
    let n = 0;
    for (const [px, pz] of POINTS) {
      if (n >= stamps.length) {
        break;
      }
      const lx = px * Math.cos(spin) - pz * Math.sin(spin) + (hexHash(t, 60 + n) - 0.5) * 2 * JITTER;
      const lz = px * Math.sin(spin) + pz * Math.cos(spin) + (hexHash(t, 90 + n) - 0.5) * 2 * JITTER;
      if (!clear(lx, lz, k) || Math.hypot(lx - peakX, lz - peakZ) < peakClear) {
        continue;
      }
      const s = stamps[n];
      const scale = s.scale[0] + (s.scale[1] - s.scale[0]) * hexHash(t, 120 + n);
      const yaw = Math.round(hexHash(t, 150 + n) * 6) * (Math.PI / 3) + 0.1;
      out.push(s.slot, c.x + lx, groundAt(shapes, t, lx, lz), c.z + lz, yaw, scale, scale, ownerCode);
      n++;
    }
  };

  for (const city of cities) {
    const t = city.hex;
    if (t < 0 || t >= owner.length) {
      continue;
    }
    // Centre hex: capitol, towers, old town, then blocks, nearest the centre first.
    const stamps: Stamp[] = [];
    const style = styleOf(grid, t);
    const tier: Tier = city.pop < MINOR_POP ? 'minor' : 'major';
    const pick = (set: readonly number[], i: number): number => set[(Math.floor(hexHash(t, 11) * set.length) + i) % set.length];
    if (city.capital) {
      stamps.push({ slot: BUILDING.CAPITOL, scale: [CAPITOL_SCALE, CAPITOL_SCALE] });
    }
    const towers = towersFor(city.pop);
    for (let i = 0; i < towers; i++) {
      stamps.push({ slot: pick(style.tower, i), scale: TOWER_SCALE });
    }
    const blocks = blocksFor(city.pop);
    const oldTown = city.pop >= OLDTOWN_POP;
    for (let i = 0; i < blocks; i++) {
      const old = oldTown && i % Math.ceil(blocks / OLDTOWN) === 0;
      stamps.push({ slot: old ? pick(style.old, i) : pick(style.block, i), scale: SCALE });
    }
    emit(t, stamps, 0, tier);

    // Districts on neighbouring land hexes (a stable pick per city).
    const districts = districtsFor(city.pop);
    const around = Math.round(blocks * DISTRICT_SHARE);
    const start = Math.floor(hexHash(t, 7) * 6);
    for (let i = 0; i < 6 && i < districts; i++) {
      const n = neighbour(t, (start + i) % 6);
      if (n < 0) {
        continue;
      }
      const d: Stamp[] = [];
      for (let j = 0; j < around; j++) {
        d.push({ slot: pick(style.block, j + i), scale: SCALE });
      }
      emit(n, d, i + 1, tier);
    }
  }

  const out = new Map<number, FeatureData>();
  for (const [key, blocks] of perChunk) {
    out.set(key, packBlocks(blocks));
  }
  return out;
}
